import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Search, User, MapPin, Mountain, X, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { searchService } from "../services/search";
import { useToast } from "../components/ui/use-toast";
import { trackEvent } from "../utils/analytics";
import type { SearchResultItem, UserSearchResult, LocationSearchResult, GymSearchResult, ClimbSearchResult } from "../types/search";

// Utility functions for local storage
const RECENT_SEARCHES_KEY = "recent-searches";
const MAX_RECENT_SEARCHES = 10;

const getRecentSearches = (): string[] => {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const addRecentSearch = (query: string): void => {
  if (!query.trim()) return;

  const recent = getRecentSearches();
  const filtered = recent.filter(item => item !== query.trim());
  const updated = [query.trim(), ...filtered].slice(0, MAX_RECENT_SEARCHES);

  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  } catch {
    // Ignore storage errors
  }
};

const removeRecentSearch = (query: string): void => {
  try {
    const recent = getRecentSearches();
    const filtered = recent.filter(item => item !== query);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(filtered));
  } catch {
    // Ignore storage errors
  }
};

// Component for gym cover image with fallback
interface GymCoverImageProps {
  src: string;
  alt: string;
  fallbackIcon: React.ReactNode;
}

const GymCoverImage = ({ src, alt, fallbackIcon }: GymCoverImageProps) => {
  return (
    <Avatar className="h-8 w-8">
      <AvatarImage src={src} alt={alt} />
      <AvatarFallback>
        {fallbackIcon}
      </AvatarFallback>
    </Avatar>
  );
};

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const { t } = useTranslation();
  const { toast } = useToast();

  // Load recent searches
  useEffect(() => {
    setRecentSearches(getRecentSearches());
  }, []);

  // Update query when URL params change (e.g., when navigating back)
  useEffect(() => {
    const urlQuery = searchParams.get('q') || '';
    if (urlQuery !== query) {
      setQuery(urlQuery);
    }
  }, [searchParams, query]);

  // Debounced search effect
  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    try {
      setLoading(true);
      const response = await searchService.search(searchQuery);
      setResults(response.data);
      trackEvent('search_performed', { query: searchQuery, results_count: response.data.length });
    } catch (error) {
      console.error('Search error:', error);
      toast({
        title: t('search.error'),
        description: t('search.errorDescription'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const searchTimeout = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [query, performSearch]);

  const handleInputChange = (value: string) => {
    setQuery(value);
    // Update URL params to maintain search state
    if (value.trim()) {
      setSearchParams({ q: value });
    } else {
      setSearchParams({});
    }
  };

  const handleInputFocus = () => {
    setInputFocused(true);
    if (query.trim()) {
      setResults([]);
    }
  };

  const handleInputBlur = () => {
    // Delay to allow clicks on recent searches
    setTimeout(() => setInputFocused(false), 200);
  };

  const handleRecentSearchClick = (searchQuery: string) => {
    setQuery(searchQuery);
    setInputFocused(false);
    // Update URL params
    setSearchParams({ q: searchQuery });
    performSearch(searchQuery);
  };

  const handleRemoveRecentSearch = (searchQuery: string, event: React.MouseEvent) => {
    event.stopPropagation();
    removeRecentSearch(searchQuery);
    setRecentSearches(getRecentSearches());
  };

  const handleResultSelect = (item: SearchResultItem) => {
    // Add to recent searches only when user clicks on a result
    if (query.trim()) {
      addRecentSearch(query.trim());
      setRecentSearches(getRecentSearches());
    }

    switch (item.type) {
      case 'user': {
        const userAttrs = item.attributes as UserSearchResult;
        if (userAttrs.slug) {
          navigate(`/users/${userAttrs.slug}`);
        }
        break;
      }
      case 'location': {
        const locationAttrsNav = item.attributes as GymSearchResult;
        if (item.subtype === 'gym') {
          if (locationAttrsNav.slug) navigate(`/gyms/${locationAttrsNav.slug}`);
        } else if (item.subtype === 'competition') {
          if (locationAttrsNav.slug) navigate(`/competitions/${locationAttrsNav.slug}`);
        } else if (item.subtype === 'crag') {
          if (locationAttrsNav.slug) navigate(`/crags/${locationAttrsNav.slug}`);
        } else if (item.subtype === 'trophy') {
          if (locationAttrsNav.slug) navigate(`/trophies/${locationAttrsNav.slug}`);
        }
        break;
      }
      case 'climb': {
        const climbAttrs = item.attributes as ClimbSearchResult;
        if (climbAttrs.location?.slug && climbAttrs.slug && climbAttrs.type) {
          const climbType = climbAttrs.type === 'Boulder' ? 'boulders' : 'routes';
          const locType = climbAttrs.location.type?.toLowerCase();
          const locationPath = locType === 'competition' ? 'competitions' : locType === 'crag' ? 'crags' : 'gyms';
          navigate(`/${locationPath}/${climbAttrs.location.slug}/${climbType}/${climbAttrs.slug}`);
        } else {
          console.warn('Missing location, climb slug, or type for navigation:', climbAttrs);
        }
        break;
      }
      default:
    }
  };

  const getResultIcon = (item: SearchResultItem) => {
    switch (item.type) {
      case 'user':
        return <User className="h-4 w-4" />;
      case 'location':
        return <MapPin className="h-4 w-4" />;
      case 'climb':
        return <Mountain className="h-4 w-4" />;
      default:
        return <Search className="h-4 w-4" />;
    }
  };

  const getResultBadge = (item: SearchResultItem) => {
    const variant = item.subtype === 'user' ? 'secondary' :
                   item.subtype === 'gym' ? 'outline' :
                   item.subtype === 'location' ? 'outline' :
                   'default';

    return (
      <Badge variant={variant} className="text-xs">
        {t(`search.types.${item.subtype}`)}
      </Badge>
    );
  };

  const renderUserResult = (item: SearchResultItem) => {
    const userAttrs = item.attributes as UserSearchResult;
    return (
      <div className="flex items-center gap-3">
        <Avatar className="h-8 w-8">
          <AvatarImage src={userAttrs.avatar_url} />
          <AvatarFallback>
            {userAttrs.username?.charAt(0)?.toUpperCase() || 'U'}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">
              {userAttrs.full_name || userAttrs.username}
            </span>
            {getResultBadge(item)}
          </div>
          {userAttrs.full_name && (
            <span className="text-sm text-muted-foreground">
              @{userAttrs.username}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderLocationResult = (item: SearchResultItem) => {
    const locationAttrs = item.attributes as LocationSearchResult;

    return (
      <div className="flex items-center gap-3">
        {/* Show logo if available, otherwise show icon */}
        {locationAttrs.logo_small_url ? (
          <GymCoverImage
            src={locationAttrs.logo_small_url}
            alt={locationAttrs.name}
            fallbackIcon={getResultIcon(item)}
          />
        ) : (
          <Avatar className="h-8 w-8">
            <AvatarFallback>
              {getResultIcon(item)}
            </AvatarFallback>
          </Avatar>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{locationAttrs.name}</span>
            {getResultBadge(item)}
          </div>
          <div className="text-sm text-muted-foreground truncate">
            {[locationAttrs.city, locationAttrs.country_name]
              .filter(Boolean)
              .join(', ')}
          </div>
        </div>
      </div>
    );
  };

  const renderClimbResult = (item: SearchResultItem) => {
    const climbAttrs = item.attributes as ClimbSearchResult;
    return (
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 bg-muted rounded-md flex items-center justify-center">
          {getResultIcon(item)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{climbAttrs.name}</span>
            {getResultBadge(item)}
          </div>
          <div className="text-sm text-muted-foreground space-y-0.5">
            {climbAttrs.location && (
              <div className="truncate">
                {t('search.atGym', { gym: climbAttrs.location.name })}
              </div>
            )}
            {climbAttrs.route_setter_name && (
              <div className="truncate">
                {t('search.setBy', { setter: climbAttrs.route_setter_name })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderResult = (item: SearchResultItem) => {
    switch (item.type) {
      case 'user':
        return renderUserResult(item);
      case 'location':
        return renderLocationResult(item);
      case 'climb':
        return renderClimbResult(item);
      default:
        return (
          <div className="flex items-center gap-3">
            {getResultIcon(item)}
            <span>{item.attributes.name}</span>
          </div>
        );
    }
  };

  const showRecentSearches = inputFocused && recentSearches.length > 0 && !query.trim();
  const showResults = (results.length > 0 || (query.trim() && !loading)) && !showRecentSearches;

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-8rem)]">
      {/* Search Input */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={t('search.placeholder')}
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          className="pl-10 h-12 text-base"
          autoFocus
        />
      </div>

      {/* Results Section */}
      <div className="flex-1 overflow-hidden">
        <h2 className="text-lg font-semibold mb-4">
          {showRecentSearches ? t('search.recentSearches') : t('search.results')}
        </h2>

        <div className="h-full overflow-y-auto">
          {/* Recent Searches */}
          {showRecentSearches && (
            <div className="space-y-2">
              {recentSearches.map((recentQuery, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent cursor-pointer group"
                  onClick={() => handleRecentSearchClick(recentQuery)}
                >
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 text-sm">{recentQuery}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                    onClick={(e) => handleRemoveRecentSearch(recentQuery, e)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Search Results */}
          {showResults && (
            <>
              {loading && (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">{t('search.searching')}</div>
                </div>
              )}

              {!loading && query.trim() && results.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 space-y-4">
                  <div className="text-muted-foreground">{t('search.noResults')}</div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-3">
                      {t('search.noResultsHelp')}
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/contact')}
                    >
                      {t('search.contactUs')}
                    </Button>
                  </div>
                </div>
              )}

              {!loading && results.length > 0 && (
                <div className="space-y-2">
                  {results.map((item) => (
                    <div
                      key={`${item.type}-${item.id}`}
                      className="p-3 rounded-lg border bg-card hover:bg-accent cursor-pointer transition-colors"
                      onClick={() => handleResultSelect(item)}
                    >
                      {renderResult(item)}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Empty State */}
          {!showRecentSearches && !showResults && !loading && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Search className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">{t('search.emptyTitle')}</h3>
              <p className="text-muted-foreground max-w-md">
                {t('search.emptyDescription')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
